import { LucideIcon } from "lucide-react";

import { Row, Text } from "@/components";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";

import { useUnreadNotifications } from "@/contexts/UnreadNotificationsContext";
import { UNREAD_NOTIFICATIONS_DISPLAY_CAP } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { NotificationMenuTone, NotificationType } from "@/types";

// Takes its colour from the agentic rating badge, tinted rather than filled —
// the solid count pill carries the colour so the row can stay quiet.
const toneToClassNames: Record<
  NotificationMenuTone,
  { button: string; label: string; pill: string; pillText: string }
> = {
  [NotificationMenuTone.EXCELLENT]: {
    button:
      "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800",
    label: "text-indigo-700",
    pill: "bg-indigo-600",
    pillText: "text-white",
  },
  [NotificationMenuTone.GREAT]: {
    button:
      "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800",
    label: "text-emerald-700",
    pill: "bg-emerald-600",
    pillText: "text-white",
  },
  [NotificationMenuTone.MENTION]: {
    button:
      "bg-primary-300 text-primary-900 hover:bg-primary-400 hover:text-primary-900",
    label: "text-primary-900",
    pill: "bg-primary-800",
    pillText: "text-white",
  },
  [NotificationMenuTone.DEFAULT]: {
    button: "text-slate-900",
    label: "text-slate-900",
    pill: "bg-primary-400",
    pillText: "text-primary-900",
  },
};

// Display elevated notifications with unique colors
export const NotificationAlertMenuItem = ({
  count,
  Icon,
  label,
  onClick,
  tone = NotificationMenuTone.DEFAULT,
  types,
}: {
  count?: number;
  Icon: LucideIcon;
  label: string;
  onClick: () => void;
  tone?: NotificationMenuTone;
  types: NotificationType[];
}) => {
  /* Database CRUD Operations */
  // Read
  const { unreadCountsByType } = useUnreadNotifications();

  /* Helpers */
  const unreadCount = types.reduce(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    (total, type) => total + (unreadCountsByType[type] || 0),
    0,
  );
  const shownCount = count ?? unreadCount;
  const countLabel =
    shownCount > UNREAD_NOTIFICATIONS_DISPLAY_CAP
      ? `${UNREAD_NOTIFICATIONS_DISPLAY_CAP.toString()}+`
      : shownCount.toString();
  const toneClassNames = toneToClassNames[tone];

  /* Render */
  const render = () => {
    if (shownCount === 0) return null;

    return (
      // One entrance animation, then it holds still — persistent motion gets
      // tuned out and reads as broken rather than urgent.
      <SidebarMenuItem className="overflow-hidden rounded-md duration-200 animate-in fade-in slide-in-from-left-1">
        <SidebarMenuButton
          className={cn("font-medium", toneClassNames.button)}
          onClick={onClick}
          tooltip={`${label} (${countLabel})`}
        >
          <Row className="w-full items-center gap-2">
            <Icon className="h-4 w-4 shrink-0" />
            {/* Text ships its own slate colour, so the label can't inherit the button's */}
            <Text
              truncate
              className={cn(
                "min-w-0 flex-1 text-sm font-medium",
                toneClassNames.label,
              )}
            >
              {label}
            </Text>
            <Row
              className={cn(
                "h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5",
                toneClassNames.pill,
              )}
            >
              <Text
                className={cn(
                  "text-[11px] font-semibold leading-none",
                  toneClassNames.pillText,
                )}
              >
                {countLabel}
              </Text>
            </Row>
          </Row>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return render();
};
